/**
 * A label layout, and the arithmetic of moving things around on it.
 *
 * Everything here is pure and measured in millimetres, which is the unit the
 * server draws in: the label is a physical object, 40 by 12 whatever the print
 * head is. Pixels appear only in the canvas that renders this, and only as a
 * scale factor.
 *
 * The functions are separated from the canvas so that the rules a person
 * actually notices — a block cannot leave the label, a block cannot become
 * nothing, dragging lands on a tenth of a millimetre — can be checked without
 * a browser, a pointer or a rendered frame.
 */

/** What a block draws. Mirrors the vocabulary of `app/labels/renderer.py`. */
export type ElementType = 'text' | 'qr' | 'barcode' | 'line';

/**
 * Where a block gets its value.
 *
 * `title`/`subtitle`/`extra` mean different things per record kind — see
 * `label_service.subject`. `mpn` and `manufacturer` are empty on a place, which
 * draws nothing rather than failing: one layout has to serve a part and a shelf.
 *
 * The clock is three sources rather than one with a format string, so the
 * format is chosen by name instead of typed as `%d.%m.%Y`. None of them carry
 * seconds: a label is not a stopwatch.
 */
export type Source =
  | 'code'
  | 'url'
  | 'title'
  | 'subtitle'
  | 'extra'
  | 'mpn'
  | 'manufacturer'
  | 'serial_number'
  | 'quantity_at_print'
  | 'date'
  | 'datetime'
  | 'time'
  | 'field_1'
  | 'field_2'
  | 'field_3'
  | 'field_4'
  | 'field_5'
  | 'field_6';

export interface LayoutElement {
  type: ElementType;
  /** Millimetres from the top-left of one label. */
  x: number;
  y: number;
  w: number;
  h: number;
  source?: Source;
  /** Fixed text instead of a source: a caption, a company name. */
  text?: string;
  size_mm?: number;
  min_size_mm?: number;
  lines?: number;
  bold?: boolean;
  align?: 'left' | 'center' | 'right';
}

export interface Layout {
  elements: LayoutElement[];
}

/** The physical label a layout is drawn on. */
export interface Media {
  width_mm: number;
  height_mm: number;
}

/**
 * The grid everything lands on.
 *
 * A tenth of a millimetre is two dots at 203 dpi — fine enough that nobody
 * fights the grid, coarse enough that the numbers in the form stay readable and
 * two blocks meant to line up actually do. Free-floating coordinates with six
 * decimals look precise and make a layout impossible to tidy.
 */
export const STEP = 0.1;

/** The smallest block worth having. Below this it cannot be grabbed again. */
export const MIN_SIZE = 1;

export function snap(value: number): number {
  return Math.round(value / STEP) * STEP;
}

/** Rounded for display and for the JSON: 12.300000000000001 helps nobody. */
export function tidy(value: number): number {
  return Math.round(value * 10) / 10;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/**
 * Move a block to a new top-left corner, keeping it on the label.
 *
 * Clamped rather than refused: a finger that leaves the label mid-drag should
 * push the block against the edge and stay in control of it, not drop it.
 */
export function moveElement(
  element: LayoutElement,
  media: Media,
  to: { x: number; y: number },
): LayoutElement {
  return {
    ...element,
    x: tidy(clamp(snap(to.x), 0, Math.max(media.width_mm - element.w, 0))),
    y: tidy(clamp(snap(to.y), 0, Math.max(media.height_mm - element.h, 0))),
  };
}

/**
 * Resize a block by its bottom-right corner.
 *
 * The top-left stays where it is, so the corner under the finger is the one
 * that moves. Never smaller than `MIN_SIZE` and never past the edge of the
 * label — a zero-width block is invisible and unrecoverable, and one hanging
 * off the label is refused by the server at the moment of saving, which is a
 * worse place to find out.
 */
export function resizeElement(
  element: LayoutElement,
  media: Media,
  size: { w: number; h: number },
): LayoutElement {
  return {
    ...element,
    w: tidy(clamp(snap(size.w), MIN_SIZE, Math.max(media.width_mm - element.x, MIN_SIZE))),
    h: tidy(clamp(snap(size.h), MIN_SIZE, Math.max(media.height_mm - element.y, MIN_SIZE))),
  };
}

/** Replace one block in a layout, leaving the rest untouched. */
export function withElement(layout: Layout, index: number, element: LayoutElement): Layout {
  return {
    ...layout,
    elements: layout.elements.map((current, at) => (at === index ? element : current)),
  };
}

export function removeElement(layout: Layout, index: number): Layout {
  return { ...layout, elements: layout.elements.filter((_, at) => at !== index) };
}

/**
 * A new block, placed where it can be seen and grabbed.
 *
 * Near the top-left rather than in the middle: on a 12 mm tape the middle is
 * already occupied by whatever the layout is for, and a new block dropped on
 * top of an existing one looks like nothing happened at all.
 */
export function newElement(
  type: ElementType,
  media: Media,
  defaultSource?: Source,
): LayoutElement {
  const side = Math.min(media.height_mm - 1, 10);
  const base = { type, x: 1, y: 1 };

  switch (type) {
    case 'qr':
      return { ...base, source: defaultSource ?? 'url', w: Math.max(side, MIN_SIZE), h: Math.max(side, MIN_SIZE) };
    case 'barcode':
      return {
        ...base,
        source: defaultSource ?? 'code',
        w: Math.max(media.width_mm - 2, MIN_SIZE),
        h: Math.max(Math.min(media.height_mm / 3, 8), MIN_SIZE),
      };
    case 'line':
      return { ...base, w: Math.max(media.width_mm - 2, MIN_SIZE), h: 0.3 };
    default:
      return {
        ...base,
        source: defaultSource ?? 'title',
        w: Math.max(media.width_mm - 2, MIN_SIZE),
        h: Math.max(Math.min(media.height_mm / 3, 6), MIN_SIZE),
        size_mm: 3,
        min_size_mm: 2,
        lines: 1,
      };
  }
}

/** How a block is named in the list when it has no text of its own. */
export function elementLabel(element: LayoutElement): string {
  return element.text ? `“${element.text}”` : (element.source ?? element.type);
}
